import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import QRCode from "react-qr-code";
import { CreditCard, ShieldCheck, Gift, Building, FileText, Wallet, AlertTriangle } from 'lucide-react';

import { getAccountData, getProforma } from '../../api/BackendApi';
import CheckoutButton from '../../components/CheckoutButton';
import ActionButton from '../../components/ActionButton'; 

const IBAN = 'CZ6020100000002503514301';

export function buildQrPaymentString(amount, variableSymbol) {
    const parts = [
        "SPD",
        "1.0",
        `ACC:${IBAN}`,
        `AM:${Number(amount).toFixed(2)}`,
        "CC:CZK",
        `X-VS:${variableSymbol}`,
        `MSG:Koupě kreditů pro Recodeq`,
    ];

    return parts.join("*") + "*";
}

// Pomocná funkce pro výpočet bonusů z částky v Kč
export const getBonusDetails = (amountCZK) => {
  let bonusPercent = 0;
  if (amountCZK >= 100000) bonusPercent = 10;
  else if (amountCZK >= 75000) bonusPercent = 8;
  else if (amountCZK >= 50000) bonusPercent = 5;
  else if (amountCZK >= 25000) bonusPercent = 3;

  const bonusCredits = Math.floor(amountCZK * (bonusPercent / 100));
  const totalCredits = amountCZK + bonusCredits;

  return {
    baseAmount: amountCZK,
    bonusPercent,
    bonusCredits,
    totalCredits
  };
};

// Pomocná funkce: Kolik min. Kč musím zaplatit, abych získal X kreditů? (Včetně započítání bonusů)
export const getRequiredCZKForCredits = (targetCredits) => {
  if (targetCredits <= 0) return 0;
  
  // Vypočítáme teoretickou potřebnou částku v Kč pro každý bonusový stupeň
  // Math.max zajistí, že splníme minimální hranici pro daný bonus
  let k10 = Math.max(100000, Math.ceil(targetCredits / 1.10));
  let k8 = Math.max(75000, Math.ceil(targetCredits / 1.08));
  let k5 = Math.max(50000, Math.ceil(targetCredits / 1.05));
  let k3 = Math.max(25000, Math.ceil(targetCredits / 1.03));
  let k0 = targetCredits;

  const options = [k10, k8, k5, k3, k0];
  let bestK = Infinity;

  // Vyzkoušíme všechny možnosti a najdeme tu absolutně nejlevnější v Kč, 
  // která nám dá >= targetCredits
  for (let k of options) {
    let bonus = 0;
    if (k >= 100000) bonus = 0.10;
    else if (k >= 75000) bonus = 0.08;
    else if (k >= 50000) bonus = 0.05;
    else if (k >= 25000) bonus = 0.03;
    
    let creditsReceived = k + Math.floor(k * bonus);
    if (creditsReceived >= targetCredits && k < bestK) {
      bestK = k;
    }
  }
  return bestK;
};


const BuyCreditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fileId, setFileId] = useState(null); 
  const [amount, setAmount] = useState(1000); 
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');

  const [isAccountValid, setIsAccountValid] = useState(false); // Are invoice data filled ?
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [variableSymbol, setVariableSymbol] = useState(null);

  const presetAmounts = [500, 1000, 2000, 5000];

  // Efekt pro zachycení doporučené částky (v kreditech) z jiné stránky
  useEffect(() => {
    if (location.state?.recommendedCredits) {
      const targetCredits = Number(location.state.recommendedCredits);
      // Spočítáme nejvýhodnější částku v Kč, kterou musí uživatel zaplatit
      const requiredCZK = getRequiredCZKForCredits(targetCredits);
      
      setAmount(requiredCZK);
      setCustomAmount(requiredCZK.toString());
    }
    if (location.state?.fileId) {
      setFileId(Number(location.state.fileId))
    }
  }, [location.state]);

  useEffect(() => {
    const checkAccountData = async () => {
      try {
        const data = await getAccountData();
        
        // Zkontrolujeme všechna povinná pole 
        const requiredFields = ['name', 'street', 'houseNumber', 'city', 'postalCode', 'country'];
        const isValid = requiredFields.every(field => data[field] && data[field].trim() !== '');
        
        setIsAccountValid(isValid);
        setVariableSymbol(data.variableSymbol)
      } catch (error) {
        console.error("Nepodařilo se ověřit data účtu", error);
        setIsAccountValid(false);
      } finally {
        setIsLoadingAccount(false);
      }
    };

    checkAccountData();
  }, []);

  const handleSetAmount = (preset) => {
    setAmount(preset);
    setCustomAmount(''); 
  };

  const handleCustomAmountChange = (e) => {
    const onlyNumbers = e.target.value.replace(/\D/g, "");
    setCustomAmount(onlyNumbers);
    setAmount(Number(onlyNumbers) || 0);
  };
  const customAmountNumber = Number(customAmount);
  const isCustomAmountTooSmall = customAmount !== "" && customAmountNumber < 15;

  const bonusDetails = getBonusDetails(amount);

  return (
    <div className="flex justify-center min-h-full p-4 pt-8 pb-16 bg-gray-50">
      <div className="w-full max-w-3xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          
          {/* Hlavička */}
          <div className="flex items-center justify-center text-blue-600 mb-4">
            <Wallet size={40} className="mr-3" />
            <h2 className="text-3xl font-bold text-gray-800">Dobít kredit</h2>
          </div>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Vyberte si přednastavenou částku nebo zadejte vlastní. Kredity se ihned připíší na váš účet a můžete je využívat k analýzám.
          </p>

          {/* 1. Výběr částky */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-gray-800 mb-4">1. Zvolte částku k úhradě (Kč)</h3>
            
            {/* Preset Amounts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {presetAmounts.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleSetAmount(preset)}
                  className={`text-lg font-semibold py-4 rounded-xl border-2 transition-all ${
                    amount === preset && customAmount === ''
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-blue-600 border-gray-200 hover:border-blue-400'
                  }`}
                >
                  {preset} Kč
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="w-full">
              <label
                htmlFor="customAmount"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nebo zadejte vlastní částku v Kč
              </label>

              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="customAmount"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Např: 600"
                  className={`w-full p-4 pl-4 pr-12 text-lg border rounded-xl shadow-sm focus:ring-2 outline-none ${
                    isCustomAmountTooSmall
                      ? "border-red-400 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                />

                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                  Kč
                </span>
              </div>

              {isCustomAmountTooSmall && (
                <p className="mt-2 text-sm text-red-600">
                  Minimální částka je 15 Kč.
                </p>
              )}
            </div>
          </div>

          {/* 2. Množstevní Bonusy (Informační box) */}
          <div className="mb-10 p-6 bg-blue-50 border border-blue-100 rounded-xl">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-6 h-6 text-blue-700" />
              <h3 className="text-xl font-bold text-blue-800">Bonusy za větší nákup</h3>
            </div>
            <p className="text-blue-700 mb-4 text-sm">
              Získejte extra kredity navíc ke svému dobití. Platíte vámi zvolenou částku, ale na účet se vám připíše bonus.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="text-xs text-gray-500 mb-1">Nad 25 000 Kč</div>
                <div className="font-extrabold text-blue-600">+3 %</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="text-xs text-gray-500 mb-1">Nad 50 000 Kč</div>
                <div className="font-extrabold text-blue-600">+5 %</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="text-xs text-gray-500 mb-1">Nad 75 000 Kč</div>
                <div className="font-extrabold text-blue-600">+8 %</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-100">
                <div className="text-xs text-gray-500 mb-1">Nad 100 000 Kč</div>
                <div className="font-extrabold text-blue-600">+10 %</div>
              </div>
            </div>
          </div>

          {/* 3. Shrnutí a Výsledek s Bonusem */}
          <div className="w-full flex flex-col sm:flex-row items-center justify-between bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8">
            <div className="text-center sm:text-left mb-4 sm:mb-0">
              <p className="text-sm text-gray-500 uppercase tracking-wide font-bold mb-1">Celkem získáte na účet</p>
              <div className="flex items-end justify-center sm:justify-start gap-3 mt-1">
                <p className="text-4xl font-black text-gray-800">{bonusDetails.totalCredits}</p>
                <p className="text-lg font-semibold text-gray-600 pb-1">kreditů</p>
              </div>
            </div>

            <div className="text-center sm:text-right flex flex-col items-center sm:items-end justify-center">
              {bonusDetails.bonusPercent > 0 ? (
                <>
                  <div className="flex items-center gap-1.5 text-green-700 bg-green-100 px-3 py-1.5 rounded-lg font-bold text-sm">
                    <Gift className="w-4 h-4" />
                    Včetně bonusu {bonusDetails.bonusPercent} %
                  </div>
                  <p className="text-sm text-gray-500 mt-2 font-medium">
                    (Zaplatíte {bonusDetails.baseAmount} Kč + {bonusDetails.bonusCredits} kreditů zdarma)
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500 mt-2 font-medium">
                  (K úhradě: {bonusDetails.baseAmount} Kč)
                </p>
              )}
            </div>
          </div>

          {/* 4. Platební metody (Viditelné pouze pro přihlášené) */}
          <div className="w-full">
              <h4 className="font-semibold text-lg text-gray-800 mb-4">2. Zvolte metodu platby</h4>
              
              {/* Tabs Header */}
              <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                <button 
                  type="button"
                  onClick={() => setPaymentMethod('card')} 
                  className={`flex items-center gap-2 px-5 py-3 font-semibold transition-colors whitespace-nowrap ${paymentMethod === 'card' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="hidden sm:inline">Kartou</span>
                </button>
                <button 
                  type="button"
                  onClick={() => setPaymentMethod('transfer')} 
                  className={`flex items-center gap-2 px-5 py-3 font-semibold transition-colors whitespace-nowrap ${paymentMethod === 'transfer' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <Building className="w-5 h-5" />
                  <span className="hidden sm:inline">Převodem</span>
                </button>
                <button 
                  type="button"
                  onClick={() => setPaymentMethod('invoice')} 
                  className={`flex items-center gap-2 px-5 py-3 font-semibold transition-colors whitespace-nowrap ${paymentMethod === 'invoice' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="hidden sm:inline">Fakturou</span>
                </button>
              </div>

              {/* Tabs Content */}
              <div className="w-full p-6 bg-white border border-gray-200 rounded-xl shadow-sm mb-6">
                
                {paymentMethod === 'card' && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-gray-600 text-sm max-w-md">
                      Platba probíhá okamžitě a bezpečně přes platební bránu Stripe. Kredity budou po úspěšné platbě <strong>ihned</strong> připsány na váš účet.
                      <div className="flex items-center text-green-700 mt-2">
                        <ShieldCheck size={16} className="mr-1 flex-shrink-0" />
                        <span className="text-xs font-medium">Bezpečná platba (údaje o kartě neukládáme)</span>
                      </div>
                    </div>
                    <div className="w-full sm:w-auto shrink-0">
                      {isAccountValid ? (
                        <CheckoutButton amount={bonusDetails.baseAmount} fileId={fileId} isTemporary={false} disabled={false} /> 
                      ) : (
                        <ActionButton
                          onClick={() => {
                            navigate('/fill-invoice-data', { 
                              state: { 
                                amount: bonusDetails.baseAmount, 
                                fileId: fileId 
                              } 
                            });
                          }} 
                          disabled={false}
                          className="w-1/2"
                        >
                          Pokračovat k platbě
                        </ActionButton> 
                      )}
                    </div>
                  </div>
                )}

                {paymentMethod === 'transfer' && (
                  <div className="w-full rounded-xl border border-blue-100 bg-blue-50/60 p-5">
                    {!isLoadingAccount && !isAccountValid && (
                      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="text-amber-600 w-6 h-6 flex-shrink-0 mt-0.5" />
                        <div>
                          <h5 className="text-amber-800 font-bold mb-1">Chybí fakturační údaje</h5>
                          <p className="text-amber-700 text-sm">
                            Pro vystavení dokladu a provedení platby potřebujeme znát vaše fakturační údaje (IČO, adresa atd.). 
                            Prosím, <Link to="/account/edit" className="font-bold underline hover:text-amber-900">doplňte je v nastavení účtu</Link>.
                          </p>
                        </div>
                      </div>
                    )}

                    {isAccountValid && (
                      <div>
                        <div className="mb-4">
                          <h3 className="text-base font-semibold text-gray-900">
                            Platba bankovním převodem
                          </h3>
                          <p className="mt-1 text-sm text-gray-600">
                            Zadejte platbu s následujícími údaji:
                          </p>
                        </div>

                        <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm text-gray-500">Číslo účtu</span>
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              2503514301/2010
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm text-gray-500">Variabilní symbol</span>
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              {variableSymbol}
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm text-gray-500">Částka</span>
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              {bonusDetails.baseAmount} Kč
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm text-gray-500">Nebo můžete využít platbu QR kódem, který naskenujete aplikací mobilního bankovnictví</span>
                            <QRCode value={buildQrPaymentString(bonusDetails.baseAmount, variableSymbol )} size={180} />
                          </div>
                        </div>

                        <p className="mt-4 text-sm leading-relaxed text-gray-600">
                          Kredity budou na Váš účet připsány do hodiny od přijetí částky na náš bankovní účet.
                        </p>
                      </div>
                    )}

                    
                  </div>
                )}

                {paymentMethod === 'invoice' && (
                  <div>
                    {!isLoadingAccount && !isAccountValid && (
                      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="text-amber-600 w-6 h-6 flex-shrink-0 mt-0.5" />
                        <div>
                          <h5 className="text-amber-800 font-bold mb-1">Chybí fakturační údaje</h5>
                          <p className="text-amber-700 text-sm">
                            Pro vystavení dokladu a provedení platby potřebujeme znát vaše fakturační údaje (IČO, adresa atd.). 
                            Prosím, <Link to="/account/edit" className="font-bold underline hover:text-amber-900">doplňte je v nastavení účtu</Link>.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                      <div className="text-gray-600 text-sm max-w-md">
                        Vystavíme vám zálohovou fakturu k proplacení. Jakmile evidujeme platbu, <strong>připíšeme vám kredity</strong> a zašleme finální daňový doklad.
                      </div>
                      <div className="w-full sm:w-auto shrink-0">
                        <ActionButton 
                          disabled={!isAccountValid}
                          onClick={() => getProforma(bonusDetails.baseAmount)} 
                          className="w-full flex items-center justify-center gap-2 whitespace-nowrap">
                          <FileText className="w-5 h-5" />
                          Vystavit fakturu
                        </ActionButton>
                      </div>
                    </div>

                  </div>
                  
                )}

              </div>
              
              {/* Informace o dokladech */}
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Daňové doklady ke všem platbám jsou automaticky ke stažení v sekci <Link to="/payments/history" className="text-blue-600 hover:underline">Historie transakcí</Link>.
                </p>
              </div>

            </div>

        </div>
      </div>
    </div>
  );
};

export default BuyCreditPage;